import { apiClient, type Paginated } from '@/core/api';
import type {
  Department,
  DepartmentInput,
  Employee,
  EmployeeInput,
  Payslip,
  SalaryRecord,
} from './types';

export const BASE = '/hrm/employees/';
const SALARY = '/hrm/salary-records/';
const PAYSLIPS = '/finance/payslips/';
const DEPARTMENTS = '/hrm/departments/';

export interface EmployeeListParams {
  page?: number;
  search?: string;
  status?: string;
  employment_type?: string;
  department?: number;
  /** Include terminated employees (roster only; pickers/forms hide them by
   *  default). Ignored when an explicit `status` filter is set. */
  include_terminated?: boolean;
  /** DRF `OrderingFilter` value, e.g. `last_name` or `-hire_date`. */
  ordering?: string;
}

// ---- Departments (picker + grouping) ---------------------------------------

export async function listDepartments(search?: string): Promise<Department[]> {
  const { data } = await apiClient.get<Paginated<Department> | Department[]>(DEPARTMENTS, {
    params: search ? { search } : undefined,
  });
  return Array.isArray(data) ? data : data.results;
}

export async function getDepartment(id: string | number): Promise<Department> {
  const { data } = await apiClient.get<Department>(`${DEPARTMENTS}${id}/`);
  return data;
}

export async function createDepartment(input: DepartmentInput): Promise<Department> {
  const { data } = await apiClient.post<Department>(DEPARTMENTS, input);
  return data;
}

export async function updateDepartment(
  id: number,
  input: Partial<DepartmentInput>,
): Promise<Department> {
  const { data } = await apiClient.patch<Department>(`${DEPARTMENTS}${id}/`, input);
  return data;
}

export async function deleteDepartment(id: number): Promise<void> {
  await apiClient.delete(`${DEPARTMENTS}${id}/`);
}

export async function listEmployees(params: EmployeeListParams = {}): Promise<Paginated<Employee>> {
  const { data } = await apiClient.get<Paginated<Employee>>(BASE, { params });
  return data;
}

export async function getEmployee(id: string | number): Promise<Employee> {
  const { data } = await apiClient.get<Employee>(`${BASE}${id}/`);
  return data;
}

export async function createEmployee(input: EmployeeInput): Promise<Employee> {
  const { data } = await apiClient.post<Employee>(BASE, input);
  return data;
}

export async function updateEmployee(
  id: string | number,
  input: Partial<EmployeeInput>,
): Promise<Employee> {
  const { data } = await apiClient.patch<Employee>(`${BASE}${id}/`, input);
  return data;
}

export async function deleteEmployee(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

/** Upload (or replace) an employee photo via multipart PATCH — mirrors the
 * org-logo / cabinet-profile upload pattern. */
export async function uploadEmployeePhoto(id: string | number, photo: File): Promise<Employee> {
  const form = new FormData();
  form.append('photo', photo);
  const { data } = await apiClient.patch<Employee>(`${BASE}${id}/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function listSalaryRecords(employee: number): Promise<Paginated<SalaryRecord>> {
  const { data } = await apiClient.get<Paginated<SalaryRecord>>(SALARY, { params: { employee } });
  return data;
}

/** Write payload — mirrors SalaryRecordWriteSerializer (base_amount derives server-side). */
export interface SalaryRecordInput {
  employee: number;
  effective_date: string;
  amount: string;
  currency: string;
  fx_rate_to_base: string;
  note?: string;
}

export async function createSalaryRecord(input: SalaryRecordInput): Promise<SalaryRecord> {
  const { data } = await apiClient.post<SalaryRecord>(SALARY, input);
  return data;
}

/** Payslips (salary actually paid) for one employee — finance-roles only;
 * HR is 403'd by the payroll endpoint, so the tab is gated client-side too. */
export async function listEmployeePayslips(employee: number): Promise<Paginated<Payslip>> {
  const { data } = await apiClient.get<Paginated<Payslip>>(PAYSLIPS, {
    params: { employee, ordering: '-payroll_run__period_start' },
  });
  return data;
}
